import * as React from "react";
import { GlassSurface, cn } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for GlassSurface (#152).
 *
 * VERIFY CAVEAT — `backdrop-filter` does NOT render in flat html-to-image / PDF
 * capture: those show a flat fill, never the blur. This MUST be verified in a
 * real browser. To make the frost + sheen + float actually visible, every glass
 * surface here sits OVER a mood-lit backdrop (radial-gradient lighting) with
 * text + tiles UNDER the glass. Toggle light/dark via the playground chrome and
 * re-confirm the five glass cues flip (see ReadOut below).
 */

/** A mood-lit backstop: layered radial gradients + content tiles UNDER the glass. */
function MoodBackdrop({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-10"
      style={{
        // Mood lighting recipe (lifted from the design file's .df/.lf nodes):
        // warm + cool radial pools over the canvas so the frost has colour to
        // blur and the sheen reads. Token-free on purpose — this is demo scenery,
        // not part of the primitive's surface.
        background:
          "radial-gradient(60% 80% at 18% 12%, rgba(30,111,224,.30), transparent 60%)," +
          "radial-gradient(55% 75% at 88% 10%, rgba(99,102,241,.22), transparent 58%)," +
          "radial-gradient(65% 85% at 72% 100%, rgba(56,189,248,.20), transparent 62%)," +
          "linear-gradient(135deg, hsl(var(--background)), hsl(var(--muted)))",
      }}
    >
      {/*
        Scenery that sits UNDER the glass so the blur + saturation are visible.
        Issue #159: this must be an EARLIER-PAINTED sibling of the SAME relative
        container the GlassSurface sits in, with NO explicit z-index on either
        sibling. The glass layer's backdrop-filter only samples what is painted
        behind it within its backdrop-root; any intervening stacking context
        (an `isolate` wrapper, OR a positioned element with a non-auto z-index)
        would wall the scenery off and the frost would read as a flat fill. So
        we rely on DOM paint order only — scenery first (absolute, z-index auto),
        children second (relative, z-index auto) — and the MoodBackdrop container
        is `relative` with z-index auto so it does NOT bound the backdrop-root.
      */}
      <div aria-hidden className="pointer-events-none absolute inset-0 p-8">
        <p className="max-w-md text-2xl font-semibold leading-snug text-foreground/80">
          Text and tiles sit behind the glass — the frost should blur and
          saturate this, not paint over a flat fill.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {["#1E6FE0", "#3B82F6", "#6366F1", "#0EA5E9"].map((c) => (
            <div
              key={c}
              className="h-16 w-16 rounded-xl"
              style={{ background: c }}
            />
          ))}
        </div>
      </div>
      {/* children (the GlassSurface) paint above the scenery by DOM order in
          the SAME container — `relative` only, no z-index, so no intermediate
          stacking context bounds the glass layer's backdrop-root. */}
      <div className="relative">{children}</div>
    </div>
  );
}

/** Live readout of the five cues + the backdrop-filter support flag. */
function ReadOut() {
  const [supported, setSupported] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    setSupported(
      typeof CSS !== "undefined" &&
        (CSS.supports("backdrop-filter", "blur(1px)") ||
          CSS.supports("-webkit-backdrop-filter", "blur(1px)")),
    );
  }, []);
  return (
    <div className="space-y-2 font-mono text-xs text-muted-foreground">
      <div>
        backdrop-filter supported:{" "}
        <b className="text-foreground">{String(supported)}</b>
        {supported === false
          ? " → graceful fallback: the --glass fill + --glassBack backing keep the panel legible (a translucent solid, not a transparent hole)."
          : null}
      </div>
      <div className="text-amber-600 dark:text-amber-400">
        Verify in a real browser — capture tools (html-to-image / PDF) show a
        flat fill, never the blur.
      </div>
      <ol className="ml-4 list-decimal space-y-0.5 text-foreground/70">
        <li>panel content/tiles BEHIND the glass are visibly blurred + saturated</li>
        <li>the blue --glassSheen lumen glows top-right</li>
        <li>the inset rim (--glassBd) + top highlight (--glassHi) read as a glass edge</li>
        <li>the --dropFloat shadow lifts the panel deeper than a .raise button</li>
        <li>the composer variant shows the --acLine accent keyline</li>
      </ol>
    </div>
  );
}

export function GlassSurfaceDemo() {
  return (
    <Section
      id="glass-surface"
      title="GlassSurface"
      description="Frosted-glass elevation for FLOATING surfaces (the AppShell Brain, the chat composer, ArtifactFocus) over a flush mood-lit base. Two stacked, identically notch-clipped layers: a backing layer casting the --dropFloat shadow and a glass layer carrying the backdrop-filter frost + sheen + inset rim. backdrop-filter and filter never share a node (they cancel)."
    >
      <VariantBlock label="Panel variant — floating frosted surface (--glassBd rim), over mood lighting">
        <MoodBackdrop>
          <GlassSurface
            variant="panel"
            nx="l"
            as="section"
            wrapperClassName="max-w-md"
            className="p-6"
          >
            <h3 className="text-title text-foreground">Brain</h3>
            <p className="mt-2 text-body text-foreground/75">
              A floating frosted panel. The blur reveals the colour pools and
              tiles beneath; the blue lumen glows from the top-right; the inset
              rim and top highlight read as a glass edge; and the --dropFloat
              shadow lifts it off the lit base.
            </p>
            <div className="mt-4 flex gap-2">
              <span className="rounded-full bg-foreground/10 px-3 py-1 text-caption text-foreground/80">
                clip-safe float
              </span>
              <span className="rounded-full bg-foreground/10 px-3 py-1 text-caption text-foreground/80">
                inset rim
              </span>
            </div>
          </GlassSurface>
        </MoodBackdrop>
      </VariantBlock>

      <VariantBlock label="Composer variant — raised glass input bar (--acLine accent keyline)">
        <MoodBackdrop>
          <GlassSurface
            variant="composer"
            nx="m"
            as="form"
            wrapperClassName="max-w-lg"
            className="flex items-center gap-3 p-3"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="text"
              placeholder="Ask Aiko anything…"
              className="min-w-0 flex-1 bg-transparent text-body text-foreground placeholder:text-foreground/40 focus:outline-none"
            />
            <button
              type="submit"
              className={cn(
                "rounded-full bg-accent px-4 py-1.5 text-label font-medium text-accent-foreground",
              )}
            >
              Send
            </button>
          </GlassSurface>
        </MoodBackdrop>
      </VariantBlock>

      <VariantBlock label="Composes with a width-animating parent (the Brain) — no clip/shadow flicker">
        <MoodBackdrop>
          <GlassSurface
            variant="panel"
            nx="l"
            wrapperClassName="w-64 [will-change:width] transition-[width] duration-500 ease-smooth-out hover:w-96"
            className="p-5"
          >
            <p className="text-body text-foreground/80">
              Hover to animate width on the wrapper (via wrapperClassName). The
              backdrop-filter + drop-shadow live on the inner layers, so the
              frost and float track the resize without flicker.
            </p>
          </GlassSurface>
        </MoodBackdrop>
      </VariantBlock>

      <VariantBlock label="Environment + verification checklist">
        <ReadOut />
      </VariantBlock>
    </Section>
  );
}
